import { Currency } from "@redotech/money/currencies";
import { CURRENCY_FORMAT } from "./money";
import {
  ButtonSettingPerProduct,
  CoverageText,
  NonCoverageButtonText,
  TitleSubtitle,
  WidgetFees,
} from "./widget";

export class StorefrontCheckoutTextUtils {
  private static fillTemplateVariables({
    text,
    currency,
    coveragePrice,
    cartTotal,
    cartTotalPreDiscount,
  }: {
    text: string;
    currency: Currency;
    coveragePrice: number;
    cartTotal: number;
    cartTotalPreDiscount: number;
  }): string {
    let textWithVariables = text.replaceAll(
      "%price%",
      CURRENCY_FORMAT(currency || "USD").format(coveragePrice),
    );
    textWithVariables = textWithVariables.replaceAll(
      "%cartTotal%",
      CURRENCY_FORMAT(currency || "USD").format(cartTotal),
    );
    textWithVariables = textWithVariables.replaceAll(
      "%combinedPrice%",
      CURRENCY_FORMAT(currency || "USD").format(coveragePrice + cartTotal),
    );
    textWithVariables = textWithVariables.replaceAll(
      "%cartTotalPreDiscount%",
      CURRENCY_FORMAT(currency || "USD").format(cartTotalPreDiscount),
    );
    textWithVariables = textWithVariables.replaceAll(
      "%combinedPricePreDiscount%",
      CURRENCY_FORMAT(currency || "USD").format(
        cartTotalPreDiscount + coveragePrice,
      ),
    );

    return textWithVariables;
  }

  static getTextByProduct(
    textByProduct: NonCoverageButtonText | undefined,
    {
      fees,
      hasProtectionReturnsAndSplit,
      cartEligibleProtection,
      cartEligibleReturns,
      currency,
      coveragePrice,
      cartTotal,
      cartTotalPreDiscount,
    }: {
      fees: WidgetFees;
      hasProtectionReturnsAndSplit: boolean;
      cartEligibleReturns: boolean;
      cartEligibleProtection: boolean;
      currency: Currency;
      coveragePrice: number;
      cartTotal: number;
      cartTotalPreDiscount: number;
    },
  ): string | undefined {
    if (!textByProduct) {
      return undefined;
    }

    const { returns, packageProtection, both } = textByProduct;

    let finalText: string | undefined;
    if (fees.mainType === "return") {
      finalText = returns;
    } else if (fees.mainType === "return,package_protection") {
      finalText = both;
    } else if (fees.mainType === "package_protection") {
      finalText = packageProtection;
    }

    if (hasProtectionReturnsAndSplit) {
      if (cartEligibleReturns && cartEligibleProtection) {
        finalText = both;
      } else if (cartEligibleProtection) {
        finalText = packageProtection;
      } else {
        finalText = returns;
      }
    }

    if (finalText) {
      finalText = this.fillTemplateVariables({
        text: finalText,
        currency,
        coveragePrice,
        cartTotal,
        cartTotalPreDiscount,
      });
    }

    return finalText;
  }

  static getCoverageTextAndSubtext(
    coverageText: CoverageText | undefined,
    textInfo: {
      fees: WidgetFees;
      hasProtectionReturnsAndSplit: boolean;
      cartEligibleReturns: boolean;
      cartEligibleProtection: boolean;
      currency: Currency;
      coveragePrice: number;
      cartTotal: number;
      cartTotalPreDiscount: number;
    },
  ): TitleSubtitle {
    if (!coverageText) {
      return {};
    }

    const titleText: string | undefined = this.getTextByProduct(
      {
        returns: coverageText.returns?.titleText,
        packageProtection: coverageText.packageProtection?.titleText,
        both: coverageText.both?.titleText,
      },
      textInfo,
    );
    const subtitleText: string | undefined = this.getTextByProduct(
      {
        returns: coverageText.returns?.subtitleText,
        packageProtection: coverageText.packageProtection?.subtitleText,
        both: coverageText.both?.subtitleText,
      },
      textInfo,
    );

    return { titleText, subtitleText };
  }

  private static defaultCheckoutButtonTexts: TitleSubtitle = {
    titleText: "Checkout+ | %combinedPrice%",
    subtitleText: "",
  };

  /** Source of truth for storefront default text when the merchants' texts are empty. Also for AB test placeholders & coverage preview defaults. */
  static defaultCheckoutTexts: {
    coverageButtonText: CoverageText;
    nonCoverageButtonText: NonCoverageButtonText;
    cardText: CoverageText;
  } = {
    coverageButtonText: {
      returns: this.defaultCheckoutButtonTexts,
      packageProtection: this.defaultCheckoutButtonTexts,
      both: this.defaultCheckoutButtonTexts,
    },
    nonCoverageButtonText: {
      returns: "Checkout without free returns",
      packageProtection: "Checkout without package protection",
      both: "Checkout without free returns & package protection",
    },

    cardText: {
      returns: {
        titleText: "Checkout+",
        subtitleText: `Free returns on your order`,
      },
      packageProtection: {
        titleText: "Checkout+",
        subtitleText: "Package protection on your order",
      },
      both: {
        titleText: "Checkout+",
        subtitleText: `Free returns + package protection`,
      },
    },
  };

  static buttonSettingsPerProductToCoverageButtonText: Record<
    ButtonSettingPerProduct,
    keyof typeof StorefrontCheckoutTextUtils.defaultCheckoutTexts.coverageButtonText
  > = {
    [ButtonSettingPerProduct.RETURN]: "returns",
    [ButtonSettingPerProduct.PACKAGE_PROTECTION]: "packageProtection",
    [ButtonSettingPerProduct.BOTH]: "both",
  };

  static getDefaultInputTexts(
    buttonSettingPerProduct: ButtonSettingPerProduct,
  ): {
    defaultCoverageButtonTextToUse: TitleSubtitle;
    defaultNonCoverageButtonTextToUse: string;
    defaultCardTextToUse: TitleSubtitle;
  } {
    const defaultCoverageButtonTextToUse =
      this.defaultCheckoutTexts.coverageButtonText[
        this.buttonSettingsPerProductToCoverageButtonText[
          buttonSettingPerProduct
        ]
      ];
    const defaultNonCoverageButtonTextToUse =
      this.defaultCheckoutTexts.nonCoverageButtonText[
        this.buttonSettingsPerProductToCoverageButtonText[
          buttonSettingPerProduct
        ]
      ];
    const defaultCardTextToUse =
      this.defaultCheckoutTexts.cardText[
        this.buttonSettingsPerProductToCoverageButtonText[
          buttonSettingPerProduct
        ]
      ];

    /** We can gurantee the default texts are not undefined because we have a default text for each button setting per product */
    return {
      defaultCoverageButtonTextToUse: defaultCoverageButtonTextToUse!,
      defaultNonCoverageButtonTextToUse: defaultNonCoverageButtonTextToUse!,
      defaultCardTextToUse: defaultCardTextToUse!,
    };
  }
}
